import React, { useState } from 'react';
import './SpotifySection.css';
import MessageSection from '../messageSection/MessageSection';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function SpotifySection({ playlistName, messageSection }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [embedUrl, setEmbedUrl] = useState('');
  const [error, setError] = useState('');
  const [hasSelected, setHasSelected] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [fade, setFade] = useState('in');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const res = await fetch(`${API_URL}/api/spotify/search?query=${encodeURIComponent(search)}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setResults(data.results || []);
    } catch (err) {
      setError('Error searching Spotify');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (track) => {
    setFade('out');
    setTimeout(async () => {
      const url = `https://open.spotify.com/embed/track/${track.id}`;
      setEmbedUrl(url);
      setResults([]);
      setSearch('');
      setHasSelected(true);
      setIframeLoading(true);
      setFade('in');
      // POST to backend
      try {
        await fetch(`${API_URL}/api/spotify/add-track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            playlistName: playlistName,
            trackUri: `spotify:track:${track.id}`
          })
        });
      } catch (err) {
        // Optionally handle error
      }
    }, 350);
  };

  const handleRecommendAnother = () => {
    setFade('out');
    setTimeout(() => {
      setHasSelected(false);
      setEmbedUrl('');
      setSearch('');
      setResults([]);
      setError('');
      setIframeLoading(false);
      setFade('in');
    }, 350);
  };

  return (
    <div className="spotify-section">
      <MessageSection
        header={messageSection?.header}
        icon={{
          viewBox: "0 0 168 168",
          svg_path: "M84,0C37.7,0,0,37.7,0,84s37.7,84,84,84s84-37.7,84-84S130.3,0,84,0z M122.9,120.5c-1.5,2.5-4.8,3.3-7.3,1.9 c-20.1-12.3-45.4-15.1-75.3-8.4c-2.8,0.6-5.6-1.2-6.2-4c-0.6-2.8,1.2-5.6,4-6.2c33.2-7.4,61.9-4.1,84.7,10 C123.4,114.7,124.4,118.1,122.9,120.5z M132.2,97.4c-1.9,3.1-5.9,4-9,2.1c-23-14.1-58.1-18.2-85.3-10.1c-3.4,1-7-0.9-8-4.3 c-1-3.4,0.9-7,4.3-8c32.1-9.4,72.5-5,99.1,11.5C133.1,90.3,134.1,94.3,132.2,97.4z M134.1,74.1c-27.1-16.2-71.9-17.7-98.1-9.8 c-4,1.2-8.2-1-9.4-5c-1.2-4,1-8.2,5-9.4c30.4-9.1,80-7.3,111.5,11.1c3.6,2.1,4.8,6.8,2.6,10.4C143.5,75.1,138,76.9,134.1,74.1z",
          forceColor: true
        }}
        colors={{
          svg: '#1db954',
          header: '#fff',
          paragraph: '#fff'
        }}
        paragraphs={messageSection?.paragraphs}
      />
      {/* Removed title rendering */}
      <div className={`spotify-fade-wrapper fade-${fade}`}>
        {!hasSelected ? (
          <>
            <form className="spotify-search-form" onSubmit={handleSearch} autoComplete="off">
              {/* Inline style tag to force placeholder color white for this input */}
              <style>{`
                .spotify-search-input::placeholder {
                  color: #fff !important;
                  opacity: 1 !important;
                }
              `}</style>
              <input
                className="spotify-search-input"
                type="text"
                placeholder="Search for a Spotify track..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ color: '#fff', background: '#232526' }}
              />
              <button className="spotify-search-btn" type="submit" disabled={loading || !search.trim()}>
                {loading ? <span className="searching-text">Searching...</span> : 'Search'}
              </button>
            </form>
            {error && <div className="spotify-error">{error}</div>}
            {results.length > 0 && (
              <ul className="spotify-results-list">
                {results.map(track => (
                  <li key={track.id} className="spotify-result-item" onClick={() => handleSelect(track)}>
                    <span className="spotify-track-name">{track.name}</span>
                    <span className="spotify-track-artist">{track.artist}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
        {embedUrl && (
          <>
            <div className="spotify-embed-wrapper" style={{ position: 'relative', minHeight: 80 }}>
              {iframeLoading && (
                <div className="spotify-loader" style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
                  <div className="spotify-spinner">
                    <div className="spotify-bar bar1"></div>
                    <div className="spotify-bar bar2"></div>
                    <div className="spotify-bar bar3"></div>
                  </div>
                </div>
              )}
              <iframe
                src={embedUrl}
                width="100%"
                height="80"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="Spotify Player"
                style={iframeLoading ? { opacity: 0 } : { opacity: 1, transition: 'opacity 0.3s' }}
                onLoad={() => setIframeLoading(false)}
              ></iframe>
            </div>
            {!iframeLoading && (
              <>
                <button className="spotify-recommend-btn" onClick={handleRecommendAnother}>
                  Recommend one more song
                </button>
                <div className="spotify-thankyou-message fade-in">Thanks for the suggestion!</div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export { SpotifySection as default };